// Third-party
import React from 'react';
import classNames from 'classnames';
import { Dropdown } from 'react-bootstrap';

// Components
import { MenuToggle } from '@/views/components/buttons';
import LegacyComparisonCardMenu from './LegacyComparisonCardMenu';

// Utils
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { getHoldingsSymbols, isBenchmark, isMissingBenchmark, shouldExpandPortfolioInReadOnly } from '@/utils/comparison';
import { PORTFOLIO_TYPES } from '@/utils/portfolios';
import { COMPARISON_ITEM_TYPES, YieldParamsType } from '@/utils/constants';
import RateLimitedComponent from '@/views/components/RateLimitedComponent';
import { RateLimitType } from '@/redux/modules/types/rate-limits';
import { ErrorTooltip } from '@/views/components/tooltips';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { ComparisonItemCategory } from '@/redux/modules/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import User from '@/types/user';
import useThemeVersion from '@/hooks/useThemeVersion';

type Props = {
  item: ComparisonItem;
  disabled?: boolean;
  canEdit: boolean;
  readOnly?: boolean;
  user?: User | null;
  onItemRemove: () => void;
  onModifyPortfolio: () => void;
  onViewPortfolio: () => void;
  onExpandPortfolio: (expand: boolean) => void;
  onClonePortfolio: () => void;
  onAddCategoryBenchmark?: () => void;
  onCloneBenchmark: () => void;
  markupMode?: boolean;
  incomeStream?: boolean;
  showPortfolioSaveModal?: boolean;
  onModifyBenchmark: () => void;
  onSavePortfolio: (toContact?: boolean) => void;
  disableSaving?: boolean;
  scrollbarContainerRef?: HTMLElement;
  hasStickyMenu?: boolean;
  onModifyGroup: () => void;
  canEditGroup?: boolean;
  onSetCurrent?: () => void;
  onSetRecommended?: () => void;
  onDeselectCurrent?: () => void;
  onDeselectRecommended?: () => void;
  hasOtherCurrentItem?: boolean;
  isCurrentItem?: boolean;
};

type MenuItem = {
  value: React.ReactElement | string;
  className?: string;
  onClick: () => void;
  hidden?: boolean;
  icon?: string;
  disabled?: boolean;
};

class ComparisonCardMenuV2 extends React.Component<Props> {
  static defaultProps = {
    canEdit: true,
    onItemRemove: () => {},
    onModifyPortfolio: () => {},
    onViewPortfolio: () => {},
    onExpandPortfolio: () => {},
    onClonePortfolio: () => {},
    onCloneBenchmark: () => {},
    onModifyBenchmark: () => {},
    onModifyGroup: () => {},
    onSetCurrent: () => {},
    onSetRecommended: () => {},
    onDeselectCurrent: () => {},
    onDeselectRecommended: () => {},
  };

  removeItem = () => {
    this.props.onItemRemove();
  };

  editPortfolio = () => {
    this.props.onModifyPortfolio();
  };

  savePortfolioToContact = () => {
    this.props.onSavePortfolio(true);
  };

  savePortfolioAsModel = () => {
    this.props.onSavePortfolio();
  };

  clonePortfolio = () => {
    this.props.onClonePortfolio();
  };

  addCategoryBenchmark = () => {
    if (this.props.onAddCategoryBenchmark) {
      this.props.onAddCategoryBenchmark();
    }
  };

  setCurrent = () => {
    if (this.props.onSetCurrent) {
      this.props.onSetCurrent();
    }
  };

  setRecommended = () => {
    if (this.props.onSetRecommended) {
      this.props.onSetRecommended();
    }
  };

  deselectCurrent = () => {
    if (this.props.onDeselectCurrent) {
      this.props.onDeselectCurrent();
    }
  };

  deselectRecommended = () => {
    if (this.props.onDeselectRecommended) {
      this.props.onDeselectRecommended();
    }
  };

  cloneBenchmark = () => {
    this.props.onCloneBenchmark();
  };

  expandPortfolio = (expand: boolean) => {
    this.props.onExpandPortfolio(expand);
  };

  editBenchmark = () => {
    this.props.onModifyBenchmark();
  };

  modifyGroup = () => {
    this.props.onModifyGroup();
  };

  viewPortfolio = () => {
    this.props.onViewPortfolio();
  };

  getMenuItems = () => {
    const { user, item, readOnly, canEdit, markupMode, incomeStream, disableSaving, canEditGroup } = this.props;
    const isAlt = item.type === COMPARISON_ITEM_TYPES.ALT;
    const isGroup = item.type === COMPARISON_ITEM_TYPES.GROUP;
    const isPortfolio = item.type === COMPARISON_ITEM_TYPES.PORTFOLIO;
    const portfolioIsUnassigned = item.portfolioType === PORTFOLIO_TYPES.UNASSIGNED;
    const portfolioHasGicOrHisa =
      isPortfolio && getHoldingsSymbols(item.holdings).some(symbol => symbol === YieldParamsType.GIC || symbol === YieldParamsType.HISA);

    const localCanEdit = canEdit && (item.availableActions == null || item.availableActions.edit) && !item.overrideHoldings;
    const canClone = item.availableActions == null || item.availableActions.clone;

    const hideExpandPortfolio = !isPortfolio || readOnly || markupMode || incomeStream;
    const hasHideAddCategoryBenchmarkConfig = !!user?.appConfig?.comparison?.hideAddCategoryBenchmark;

    const hideSaveAsContact =
      disableSaving ||
      readOnly ||
      !localCanEdit ||
      !!item.enterprise ||
      !isPortfolio ||
      markupMode ||
      !portfolioIsUnassigned ||
      !userHasPermission(user, PERMISSIONS.PORTFOLIOS_SAVE_TO_CONTACT);

    const hideSaveAsModel =
      disableSaving ||
      readOnly ||
      !localCanEdit ||
      !!item.enterprise ||
      !isPortfolio ||
      markupMode ||
      !portfolioIsUnassigned ||
      !userHasPermission(user, PERMISSIONS.USER_MODELS_CREATE);

    const hideModifyPortfolio = readOnly || !localCanEdit || !!item.enterprise || !isPortfolio || markupMode;

    const hideEditBenchmark = readOnly || !isBenchmark(item) || markupMode || incomeStream;

    const hideClonePortfolio = readOnly || !isPortfolio || markupMode || !canClone;

    const hideAddCategoryBenchmark =
      readOnly ||
      incomeStream ||
      markupMode ||
      !userHasPermission(user, PERMISSIONS.BENCHMARKS) ||
      isBenchmark(item) ||
      isMissingBenchmark(item) ||
      isAlt ||
      isGroup ||
      !this.props.onAddCategoryBenchmark ||
      portfolioHasGicOrHisa ||
      hasHideAddCategoryBenchmarkConfig;

    const hideCloneBenchmark = readOnly || !isBenchmark(item) || item.portfolioType !== PORTFOLIO_TYPES.CUSTOM || markupMode || incomeStream;

    const hideModifyGroup = readOnly || !canEditGroup || markupMode || incomeStream || !isGroup;

    const isPortfolioExpandLimited = shouldExpandPortfolioInReadOnly(item, user);

    const canEditPortfolio = localCanEdit && !readOnly && !item.enterprise;
    const hideViewPortfolio = !isPortfolio || markupMode || (canEditPortfolio && !isPortfolioExpandLimited);

    const hideEditLimited =
      !isPortfolio ||
      markupMode ||
      !(canEdit && !readOnly && !item.enterprise) ||
      localCanEdit ||
      !item.overrideHoldings ||
      (item.overrideHoldings && !item.overrideHoldings.length);

    const hasCurrentRecommendedPermission = userHasPermission(user, PERMISSIONS.COMPARISON_SET_CURRENT_RECOMMENDED);

    // Hide category setting options if read-only, not a portfolio/fund/stock, in markup mode, or callbacks not provided
    const hideSetCurrent =
      readOnly || markupMode || incomeStream || isGroup || !this.props.onSetCurrent || this.props.isCurrentItem || !hasCurrentRecommendedPermission; // Hide if this item is already current (will show deselect instead)

    const hideDeselectCurrent =
      readOnly || markupMode || incomeStream || isGroup || !this.props.onDeselectCurrent || !this.props.isCurrentItem || !hasCurrentRecommendedPermission; // Only show for the current item

    const disableSetCurrent = this.props.hasOtherCurrentItem || item.category === ComparisonItemCategory.RECOMMENDED; // Disable if another item is current OR this item is recommended

    const hideSetRecommended =
      readOnly ||
      markupMode ||
      incomeStream ||
      isGroup ||
      !this.props.onSetRecommended ||
      item.category === ComparisonItemCategory.RECOMMENDED ||
      !hasCurrentRecommendedPermission;

    const disableSetRecommended = item.category === ComparisonItemCategory.CURRENT; // Disable if item is already current

    const hideDeselectRecommended =
      readOnly ||
      markupMode ||
      incomeStream ||
      isGroup ||
      !this.props.onDeselectRecommended ||
      item.category !== ComparisonItemCategory.RECOMMENDED ||
      !hasCurrentRecommendedPermission;

    const menuItems: MenuItem[] = [
      {
        value: (
          <RateLimitedComponent type={RateLimitType.MODELS}>
            {({ disabled, error }) => (
              <Dropdown.Item
                role="menuitem"
                as="button"
                onClick={disabled ? undefined : this.savePortfolioAsModel}
                className={classNames({ 'bottom-border': hideSaveAsContact, disabled })}
              >
                <i className="icon-ds-save" />
                <FormattedMessage id="Comparison.ItemMenu.SaveAsModel" />
                {error && <ErrorTooltip message={error} />}
              </Dropdown.Item>
            )}
          </RateLimitedComponent>
        ),
        onClick: () => {},
        hidden: hideSaveAsModel,
      },
      {
        value: formatMessage('Comparison.ItemMenu.SaveToContact'),
        className: classNames({ 'bottom-border': !hideSaveAsContact }),
        onClick: this.savePortfolioToContact,
        icon: 'save-contact',
        hidden: hideSaveAsContact,
      },
      {
        value: formatMessage('Comparison.ItemMenu.ModifyPortfolio'),
        onClick: this.editPortfolio,
        icon: 'ds-edit',
        hidden: hideModifyPortfolio,
      },
      {
        value: formatMessage('Comparison.ItemMenu.EditPortfolioLimited'),
        onClick: this.viewPortfolio,
        icon: 'ds-edit',
        hidden: hideEditLimited,
      },
      {
        value: formatMessage('Comparison.ItemMenu.ViewPortfolio'),
        onClick: this.viewPortfolio,
        icon: 'ds-preview',
        hidden: hideViewPortfolio,
      },
      {
        value: formatMessage(`Comparison.ItemMenu.Benchmark.${item.holdings.length > 1 ? 'Modify' : 'CreateCustom'}`),
        onClick: this.editBenchmark,
        icon: 'sliders',
        hidden: hideEditBenchmark,
      },
      {
        value: formatMessage(`Comparison.ItemMenu.${item.isExpanded ? 'CollapsePortfolio' : 'ExpandPortfolio'}`),
        onClick: () => this.expandPortfolio(!item.isExpanded),
        icon: 'expand',
        hidden: hideExpandPortfolio,
        disabled: isPortfolioExpandLimited,
      },
      {
        value: formatMessage('Comparison.ItemMenu.ClonePortfolio'),
        onClick: this.clonePortfolio,
        icon: 'ds-copy',
        hidden: hideClonePortfolio,
      },
      {
        value: formatMessage('Comparison.ItemMenu.AddCategoryBenchmark'),
        onClick: this.addCategoryBenchmark,
        icon: 'add-category-benchmark',
        hidden: hideAddCategoryBenchmark,
      },
      {
        value: formatMessage('Comparison.ItemMenu.CloneBenchmark'),
        onClick: this.cloneBenchmark,
        icon: 'ds-copy',
        hidden: hideCloneBenchmark,
      },
      {
        value: formatMessage('Comparison.ItemMenu.SetCurrent'),
        onClick: this.setCurrent,
        icon: 'purpose-flag-current',
        className: classNames({
          'top-border': !hideSetCurrent,
          'bottom-border': !hideSetCurrent && hideSetRecommended && hideDeselectCurrent && hideDeselectRecommended,
        }),
        hidden: hideSetCurrent,
        disabled: disableSetCurrent,
      },
      {
        value: formatMessage('Comparison.ItemMenu.SetRecommended'),
        onClick: this.setRecommended,
        icon: 'ds-star',
        className: classNames({
          'top-border': !hideSetRecommended && hideSetCurrent,
          'bottom-border': !hideSetRecommended && hideDeselectCurrent && hideDeselectRecommended,
        }),
        hidden: hideSetRecommended,
        disabled: disableSetRecommended,
      },
      {
        value: formatMessage('Comparison.ItemMenu.Deselect'),
        onClick: this.props.isCurrentItem ? this.deselectCurrent : this.deselectRecommended,
        icon: 'ds-remove',
        className: classNames({
          'top-border': (!hideDeselectCurrent || !hideDeselectRecommended) && hideSetCurrent && hideSetRecommended,
          'bottom-border': !hideDeselectCurrent || !hideDeselectRecommended,
        }),
        hidden: hideDeselectCurrent && hideDeselectRecommended,
      },
      {
        value: formatMessage('Comparison.ItemMenu.EditGroup'),
        onClick: this.modifyGroup,
        icon: 'ds-edit',
        hidden: hideModifyGroup,
      },
    ].filter(i => !i.hidden);

    if (!readOnly) {
      const hasPriorItems = menuItems.length > 0;
      menuItems.push({
        value: formatMessage('Comparison.ItemMenu.Remove'),
        className: classNames('destructive', hasPriorItems ? 'top-border' : undefined),
        icon: 'ds-remove-circle-outline',
        onClick: this.removeItem,
      });
    }

    return menuItems;
  };

  renderOptions = () =>
    this.getMenuItems().map((item, key) => {
      if (typeof item.value === 'string') {
        return (
          <Dropdown.Item key={key} role="menuitem" as="button" className={classNames(item.className)} onClick={() => item.onClick()} disabled={item.disabled}>
            <i className={`icon-${item.icon}`} />
            {item.value}
          </Dropdown.Item>
        );
      }

      return item.value;
    });

  render() {
    const { disabled, hasStickyMenu, scrollbarContainerRef } = this.props;
    const options = this.renderOptions();

    if (options.length === 0) {
      return null;
    }

    return (
      <div className="comparison-card-menu-v2" onClick={event => event.stopPropagation()}>
        <MenuToggle disabled={disabled} options={options} scrollbarContainerRef={scrollbarContainerRef} hideOnScrollAway={!hasStickyMenu} alignRight />
      </div>
    );
  }
}

const ComparisonCardMenu = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <ComparisonCardMenuV2 {...props} />;
  }
  return <LegacyComparisonCardMenu {...props} />;
};

export default ComparisonCardMenu;
